import { z } from 'zod';
import { CcyCodeSchema, IsoDate, MoneyStr } from './common.js';

// ─── Аудит целостности по архиву сообщений бота (/audit, вкладка «Целостность») ──
//
// Контракт — docs/superpowers/specs/2026-08-28-archive-audit-design.md §4.6/§5.
// `details` — размеченное объединение по `kind`: контракт между проверками
// (apps/api/src/modules/archive/checks.ts), API и вебом. Денежные величины в
// `details` — в панельной модели (инверсия уже применена сервером), как на
// «Балансах» и в модалке сверки: минус — «нам должны».

/** Виды находок проверок C1..C5 + список без привязки (спека §4.6). */
export const FindingKind = z.enum([
  'chain-break',
  'journal-orphan',
  'archive-orphan',
  'coverage-gap',
  'chat-not-archived',
  'order-no-legs',
  'order-one-leg',
  'order-gross-mismatch',
  'batch-leg-missing',
  'batch-leg-mismatch',
  'batch-leg-undeclared',
  'batch-chat-unresolved',
  'unlinked-op',
  'untracked-wallet',
]);
export type FindingKind = z.infer<typeof FindingKind>;

export const FindingSeverity = z.enum(['high', 'medium', 'low']);
export type FindingSeverity = z.infer<typeof FindingSeverity>;

/**
 * Нога операции: чат плюс сумма В ПАНЕЛЬНОЙ знаковой модели — инверсия
 * `accounts.invert_balance` уже применена сервером (см. шапку файла и спеку §4.2).
 * Сырая модель бота живёт только внутри сравнения в `checks.ts` и наружу не выходит.
 */
const LegRef = z.object({
  chatId: z.string(),
  delta: MoneyStr,
});

/**
 * `details` — размеченное объединение по `kind` (спека §4.6). У каждого
 * варианта обязателен `summary` — человекочитаемый текст, который строит
 * сервер: находка обязана остаться самодостаточной после ретеншна
 * `archive.messages`.
 */
export const FindingDetails = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('chain-break'),
    summary: z.string(),
    messageId: z.number().int(),
    prevMessageId: z.number().int().nullable(),
    expected: MoneyStr,
    actual: MoneyStr,
    diff: MoneyStr,
    // Число разрывов, схлопнутых в одну находку (>5 за чат-валюту-сутки).
    collapsed: z.number().int().optional(),
  }),
  z.object({
    kind: z.literal('journal-orphan'),
    summary: z.string(),
    externalTime: z.string().nullable(),
    amount: MoneyStr,
    actor: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('archive-orphan'),
    summary: z.string(),
    messageId: z.number().int(),
    amount: MoneyStr,
    opKind: z.enum(['delta', 'snapshot']),
  }),
  z.object({
    kind: z.literal('coverage-gap'),
    summary: z.string(),
    journalRows: z.number().int(),
    unmatched: z.number().int(),
    archiveOps: z.number().int(),
  }),
  z.object({
    kind: z.literal('chat-not-archived'),
    summary: z.string(),
    lastJournalAt: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('order-no-legs'),
    summary: z.string(),
    gross: MoneyStr,
    grossCcy: CcyCodeSchema,
    notifChatId: z.string(),
    paidAt: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('order-one-leg'),
    summary: z.string(),
    gross: MoneyStr,
    grossCcy: CcyCodeSchema,
    foundLeg: LegRef,
    missing: z.enum(['client', 'counter']),
  }),
  z.object({
    kind: z.literal('order-gross-mismatch'),
    summary: z.string(),
    gross: MoneyStr,
    counterLeg: LegRef,
    diff: MoneyStr,
  }),
  z.object({
    kind: z.literal('batch-leg-missing'),
    summary: z.string(),
    batchRef: z.string(),
    batchKind: z.enum(['fixation', 'transfer']),
    targetTitle: z.string(),
    ccy: CcyCodeSchema,
    declared: MoneyStr,
  }),
  z.object({
    kind: z.literal('batch-leg-mismatch'),
    summary: z.string(),
    batchRef: z.string(),
    targetTitle: z.string(),
    ccy: CcyCodeSchema,
    declared: MoneyStr,
    actual: MoneyStr,
    diff: MoneyStr,
  }),
  z.object({
    kind: z.literal('batch-leg-undeclared'),
    summary: z.string(),
    batchRef: z.string(),
    chatId: z.string(),
    ccy: CcyCodeSchema,
    actual: MoneyStr,
    messageId: z.number().int(),
  }),
  z.object({
    kind: z.literal('batch-chat-unresolved'),
    summary: z.string(),
    batchRef: z.string(),
    targetTitle: z.string(),
    ccy: CcyCodeSchema,
    declared: MoneyStr,
  }),
  z.object({
    kind: z.literal('unlinked-op'),
    summary: z.string(),
    messageId: z.number().int(),
    ccy: CcyCodeSchema,
    delta: MoneyStr,
    actor: z.string(),
    commandText: z.string(),
  }),
  z.object({
    // Приход на TRON-адрес, которого нет в отслеживаемых кошельках: долг
    // записан, а актив в «Позицию» не входит — она занижена на остаток адреса.
    kind: z.literal('untracked-wallet'),
    summary: z.string(),
    address: z.string(),
    // Сумма приходов (сырая модель команды бота), число операций, последняя.
    inflow: MoneyStr,
    ops: z.number().int(),
    lastAmount: MoneyStr,
    lastActor: z.string().nullable(),
    lastCommandText: z.string().nullable(),
  }),
]);
export type FindingDetails = z.infer<typeof FindingDetails>;

/**
 * Находка — строка `audit.findings`. `chatId`/`chatTitle` резолвит СЕРВЕР
 * (роль manager не имеет прав дёрнуть /chat-balances/config или
 * /telegram/chats, чтобы разрешить id в название сама). `chatId` — bigint
 * строкой (JSON-safe), как везде в панели.
 */
export const FindingDto = z.object({
  id: z.number().int(),
  kind: FindingKind,
  severity: FindingSeverity,
  chatId: z.string().nullable(),
  chatTitle: z.string().nullable(),
  ccy: CcyCodeSchema.nullable(),
  orderRef: z.string().nullable(),
  occurredAt: z.string(),
  detectedAt: z.string(),
  details: FindingDetails,
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  resolveComment: z.string().nullable(),
  autoResolved: z.boolean(),
});
export type FindingDto = z.infer<typeof FindingDto>;

/** GET /archive/findings. `configured` — источник (archiveApiUrl) не настроен. */
export const ArchiveFindingsResponse = z.object({
  configured: z.boolean(),
  items: z.array(FindingDto),
  // Курсор по (occurred_at desc, id desc), base64 от {occurredAt, id}. null — конец списка.
  nextCursor: z.string().nullable(),
});
export type ArchiveFindingsResponse = z.infer<typeof ArchiveFindingsResponse>;

/** Query GET /archive/findings. */
export const ArchiveFindingsQuery = z.object({
  status: z.enum(['open', 'resolved', 'auto']).default('open'),
  kind: FindingKind.optional(),
  // Исключить вид из выборки. Нужен разделу «Расхождения» (§6) — это «всё, кроме
  // unlinked-op», и одним `kind` такое не выражается. Без него раздел пришлось бы
  // фильтровать в браузере поверх обрезанной страницы: ежедневные unlinked-op
  // вытесняли бы настоящие находки за границу limit'а.
  excludeKind: FindingKind.optional(),
  // regex обязателен: сырой chatId уходит в BigInt() — нечисловой дал бы 500
  // вместо 400 (та же грабля описана в balance-audit/routes.ts:60).
  chatId: z.string().regex(/^-?\d+$/).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
});
export type ArchiveFindingsQuery = z.infer<typeof ArchiveFindingsQuery>;

/** Тело POST /archive/findings/:id/resolve. */
export const ResolveFindingInput = z.object({
  comment: z.string().min(1),
});
export type ResolveFindingInput = z.infer<typeof ResolveFindingInput>;

/** Строка `archive.chat_coverage` — вычислена прогоном проверок, не на лету. */
export const CoverageRow = z.object({
  chatId: z.string(),
  chatTitle: z.string().nullable(),
  day: IsoDate,
  ccy: CcyCodeSchema,
  journalRows: z.number().int(),
  matched: z.number().int(),
  archiveOps: z.number().int(),
  unparsedMessages: z.number().int(),
  journalAvailable: z.boolean(),
  // Лавина непарных строк → проверки C3/C5 за эти чат-сутки заглушены (§4.4/§4.5).
  gapped: z.boolean(),
});
export type CoverageRow = z.infer<typeof CoverageRow>;

/** GET /archive/coverage. */
export const ArchiveCoverageResponse = z.object({
  configured: z.boolean(),
  coveredFrom: z.string().nullable(),
  coveredTo: z.string().nullable(),
  unparsedTotal: z.number().int(),
  rows: z.array(CoverageRow),
});
export type ArchiveCoverageResponse = z.infer<typeof ArchiveCoverageResponse>;

/**
 * GET /archive/ops — обогащение секции «Движения в боте» в AuditChatModal (C4).
 * Ключ — `${dateTime}|${ccy}|${amount}` из строки журнала бота, собранный
 * сервером из тех же полей, что рисует клиент. Неоднозначность (две одинаковых
 * строки журнала в окне) → сервер ключ не отдаёт.
 */
export const ArchiveOpsResponse = z.object({
  configured: z.boolean(),
  enrichment: z.record(
    z.string(),
    z.object({
      orderRef: z.string().nullable(),
      counterpartyChatId: z.string().nullable(),
      counterpartyTitle: z.string().nullable(),
    }),
  ),
});
export type ArchiveOpsResponse = z.infer<typeof ArchiveOpsResponse>;

/** Пропущенная за прогон чат-сутки (гейт закрыт / журнал недоступен / ингест отстал). */
const SkippedRun = z.object({
  chatId: z.string(),
  day: z.string(),
  reason: z.string(),
});

/** POST /archive/sync — счётчики одного прогона `runArchiveChecks` (спека §4.6). */
export const ArchiveSyncResponse = z.object({
  windowFrom: z.string(),
  windowTo: z.string(),
  chatsChecked: z.number().int(),
  opened: z.number().int(),
  reopened: z.number().int(),
  autoResolved: z.number().int(),
  coverageRows: z.number().int(),
  skipped: z.array(SkippedRun),
});
export type ArchiveSyncResponse = z.infer<typeof ArchiveSyncResponse>;

// ─── Задачи агента-аудитора из панели (modules/agent-tasks) ─────────────────

/** POST /agent-tasks — поставить агенту разбор; ответ придёт в чат алертов. */
export const AgentTaskRequest = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('finding-triage'), findingId: z.number().int().positive() }),
  z.object({
    kind: z.literal('ask'),
    question: z.string().trim().min(1).max(1000),
    // Окно «Что произошло» на /balances — агенту контекст интервала.
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
  }),
]);
export type AgentTaskRequest = z.infer<typeof AgentTaskRequest>;

export const AgentTaskAccepted = z.object({
  taskId: z.number().int(),
  created: z.boolean(),
  // false — чат алертов не настроен: задача стоит, но ответ придёт без привязки.
  announced: z.boolean(),
});
export type AgentTaskAccepted = z.infer<typeof AgentTaskAccepted>;
